import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, TextField, Typography, Paper, CircularProgress, Alert, Stack,
    Divider, Checkbox, FormControlLabel, FormControl, Select, MenuItem,
    IconButton, Button
} from '@mui/material';
import * as api from './locationsApi';
import type {
    Location, User, AddressType, ContactInfoType, Address, ContactInfo, Pricelist
} from './locationsApi';
import { sx14, paperSx, FormRow, DetailHeader } from '../common/detailView';

interface Props {
    locationId: number | null;
    allowUpdate: boolean;
    isAllowedToSetAutoOrder: boolean;
}

export function LocationView({ locationId, allowUpdate, isAllowedToSetAutoOrder }: Props) {
    const { t } = useTranslation();

    const [item, setItem] = useState<Location | null>(null);
    const [users, setUsers] = useState<User[]>([]);
    const [addressTypes, setAddressTypes] = useState<AddressType[]>([]);
    const [contactInfoTypes, setContactInfoTypes] = useState<ContactInfoType[]>([]);
    const [userLocations, setUserLocations] = useState<number[]>([]);
    const [pricelists, setPricelists] = useState<Pricelist[]>([]);
    const [isEditing, setIsEditing] = useState(false);
    const [copy, setCopy] = useState<Location | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [newAddressTypeId, setNewAddressTypeId] = useState<number | ''>('');
    const [newContactInfoTypeId, setNewContactInfoTypeId] = useState<number | ''>('');

    const load = useCallback(async () => {
        if (locationId === null) {
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const [loc, usrs, addrTypes, ciTypes, userLocs, pls] = await Promise.all([
                api.getLocation(locationId),
                api.getUsers(),
                api.getAddressTypes(),
                api.getContactInfoTypes(),
                api.getUserLocations(),
                api.getPricelists(),
            ]);
            loc.addresses = loc.addresses ?? [];
            loc.contactInfos = loc.contactInfos ?? [];
            loc.users = loc.users ?? [];
            setItem(loc);
            setUsers(usrs);
            setAddressTypes(addrTypes);
            setContactInfoTypes(ciTypes);
            setUserLocations(userLocs ?? []);
            setPricelists([{ id: 0, pricelistName: '-' }, ...(pls ?? [])]);
            setIsEditing(false);
        } catch (err) {
            setError(String(err));
        } finally {
            setLoading(false);
        }
    }, [locationId]);

    useEffect(() => { load(); }, [load]);

    if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    if (error && !item) return <Alert severity="error">{error}</Alert>;
    if (!item) return <Alert severity="info">No location selected.</Alert>;

    const allowSaveLocation =
        allowUpdate && (userLocations.length === 0 || userLocations.includes(item.id));

    const startEdit = () => { setCopy(JSON.parse(JSON.stringify(item))); setIsEditing(true); };
    const cancelEdit = () => {
        if (copy) setItem(copy);
        setIsEditing(false);
        setNewAddressTypeId('');
        setNewContactInfoTypeId('');
    };

    const save = async () => {
        setSaving(true); setError(null);
        try {
            // New rows use negative ids locally (for stable React keys).
            // Backend treats Id == 0 as "new", so normalize before sending.
            const payload: Location = {
                ...item,
                addresses: item.addresses.map(a => a.id < 0 ? { ...a, id: 0 } : a),
                contactInfos: item.contactInfos.map(c => c.id < 0 ? { ...c, id: 0 } : c),
            };
            const response = await api.updateLocation(payload);
            response.addresses = response.addresses ?? [];
            response.contactInfos = response.contactInfos ?? [];
            response.users = response.users ?? [];
            setItem(response);
            setIsEditing(false);
        } catch (err) {
            setError(String(err));
        } finally {
            setSaving(false);
        }
    };

    const update = <K extends keyof Location>(field: K, value: Location[K]) => {
        setItem({ ...item, [field]: value });
    };

    const updateAddress = (id: number, patch: Partial<Address>) => {
        update('addresses', item.addresses.map(a => a.id === id ? { ...a, ...patch } : a));
    };
    const deleteAddress = (id: number) => {
        update('addresses', item.addresses.filter(a => a.id !== id));
    };
    const addAddress = () => {
        const type = addressTypes.find(t => t.id === newAddressTypeId);
        if (!type) return;
        const addr: Address = { id: -Date.now(), addressType: type };
        update('addresses', [...item.addresses, addr]);
        setNewAddressTypeId('');
    };

    const updateContactInfo = (id: number, patch: Partial<ContactInfo>) => {
        update('contactInfos', item.contactInfos.map(c => c.id === id ? { ...c, ...patch } : c));
    };
    const deleteContactInfo = (id: number) => {
        update('contactInfos', item.contactInfos.filter(c => c.id !== id));
    };
    const addContactInfo = () => {
        const type = contactInfoTypes.find(t => t.id === newContactInfoTypeId);
        if (!type) return;
        const ci: ContactInfo = { id: -Date.now(), contactInfoType: type, value: '' };
        update('contactInfos', [...item.contactInfos, ci]);
        setNewContactInfoTypeId('');
    };

    const yesNo = (v: boolean) => v ? t('common.true') : t('common.false');
    const startDate = item.startDate ? new Date(item.startDate).toISOString().slice(0, 10) : '';
    const activeUsers = [...item.users]
        .filter(u => u.isActive !== false)
        .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));

    return (
        <Paper variant="outlined" sx={paperSx}>
            <DetailHeader
                title={item.locationName ?? ''}
                isEditing={isEditing}
                isNew={false}
                saving={saving}
                canSave={allowSaveLocation}
                onEdit={allowSaveLocation ? startEdit : () => { /* noop */ }}
                onSave={save}
                onCancel={cancelEdit}
            />

            <Box p={2}>
                <Stack spacing={2}>
                    {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

                    <FormRow label={t('location.name')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{item.locationName}</Typography>
                        ) : (
                            <TextField size="small" fullWidth
                                value={item.locationName ?? ''}
                                onChange={e => update('locationName', e.target.value)} />
                        )}
                    </FormRow>

                    <FormRow label={t('location.locationnumber')}>
                        <Typography sx={sx14}>{item.locationNumber}</Typography>
                    </FormRow>

                    <FormRow label={t('user.isactive')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{yesNo(item.isActive)}</Typography>
                        ) : (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={!!item.isActive}
                                        onChange={e => update('isActive', e.target.checked)}
                                    />
                                }
                                label=""
                            />
                        )}
                    </FormRow>

                    <FormRow label={t('location.shouldGenerateAutoOrders')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{yesNo(item.shouldGenerateAutoOrders)}</Typography>
                        ) : (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={!!item.shouldGenerateAutoOrders}
                                        disabled={!isAllowedToSetAutoOrder}
                                        onChange={e => update('shouldGenerateAutoOrders', e.target.checked)}
                                    />
                                }
                                label=""
                            />
                        )}
                    </FormRow>

                    <FormRow label={t('location.type')}>
                        <Typography sx={sx14}>{item.locationType?.description}</Typography>
                    </FormRow>

                    <FormRow label={t('location.category')}>
                        <Typography sx={sx14}>{item.locationCategory?.category}</Typography>
                    </FormRow>

                    {item.salesChannel && (
                        <FormRow label={t('location.saleschannel')}>
                            <Typography sx={sx14}>{item.salesChannel.salesChannelDescription}</Typography>
                        </FormRow>
                    )}

                    {item.resellerCustomer && (
                        <FormRow label={t('location.resellerCustomer')}>
                            <Typography sx={sx14}>
                                {item.resellerCustomer.socialSecurityNumber}{' '}
                                {item.resellerCustomer.firstName}{' '}
                                {item.resellerCustomer.lastName}
                            </Typography>
                        </FormRow>
                    )}

                    {item.ResellerCostPriceFactor != null && (
                        <FormRow label={t('location.resellerCostPriceFactor')}>
                            <Typography sx={sx14}>{item.ResellerCostPriceFactor}</Typography>
                        </FormRow>
                    )}

                    <FormRow label={t('location.storemanager')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>
                                {item.storeManager
                                    ? `${item.storeManager.firstName} ${item.storeManager.lastName}`
                                    : t('location.storemanager.none')}
                            </Typography>
                        ) : (
                            <FormControl size="small" fullWidth>
                                <Select
                                    value={item.storeManager?.id ?? ''}
                                    onChange={e => {
                                        const u = users.find(x => x.id === e.target.value);
                                        update('storeManager', u ?? null);
                                    }}
                                    displayEmpty
                                >
                                    <MenuItem value=""><em>-</em></MenuItem>
                                    {users.map(u => (
                                        <MenuItem key={u.id} value={u.id}>{u.firstName} {u.lastName}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </FormRow>

                    <FormRow label={t('location.squaremeters')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{item.squareMeters}</Typography>
                        ) : (
                            <TextField size="small" type="number" fullWidth
                                value={item.squareMeters}
                                onChange={e => update('squareMeters', e.target.value === '' ? 0 : Number(e.target.value))} />
                        )}
                    </FormRow>

                    <FormRow label={t('location.startdate')}>
                        <Typography sx={sx14}>{startDate}</Typography>
                    </FormRow>

                    <FormRow label={t('location.organizationnumber')}>
                        <Typography sx={sx14}>{item.organizationNumber}</Typography>
                    </FormRow>

                    <FormRow label={t('location.resellerPriceList')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{item.resellerPriceList?.pricelistName}</Typography>
                        ) : (
                            <FormControl size="small" fullWidth>
                                <Select
                                    value={item.resellerPriceList?.id ?? 0}
                                    onChange={e => {
                                        const pl = pricelists.find(p => p.id === e.target.value);
                                        update('resellerPriceList', pl && pl.id !== 0 ? pl : null);
                                    }}
                                >
                                    {pricelists.map(p => (
                                        <MenuItem key={p.id} value={p.id}>{p.pricelistName}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </FormRow>

                    <Divider />

                    {/* Addresses */}
                    <Stack spacing={1}>
                        {item.addresses.map(addr => (
                            <FormRow key={addr.id} label={addr.addressType?.typeName ?? ''}>
                                <AddressBlock
                                    address={addr}
                                    isEditing={isEditing}
                                    onUpdate={patch => updateAddress(addr.id, patch)}
                                    onDelete={() => deleteAddress(addr.id)}
                                />
                            </FormRow>
                        ))}
                        {isEditing && addressTypes.length > 0 && (
                            <FormRow label="">
                                <Box display="flex" gap={1}>
                                    <FormControl size="small" sx={{ flex: 1 }}>
                                        <Select
                                            value={newAddressTypeId}
                                            onChange={e => setNewAddressTypeId(e.target.value as number)}
                                            displayEmpty
                                        >
                                            <MenuItem value=""><em>{t('address.chooseType')}</em></MenuItem>
                                            {addressTypes.map(at => (
                                                <MenuItem key={at.id} value={at.id}>{at.typeName}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <Button size="small" variant="contained" color="secondary"
                                        onClick={addAddress} disabled={newAddressTypeId === ''}>
                                        +
                                    </Button>
                                </Box>
                            </FormRow>
                        )}
                    </Stack>

                    <Divider />

                    {/* Contact info */}
                    <Stack spacing={1}>
                        {item.contactInfos.map(ci => (
                            <FormRow key={ci.id} label={ci.contactInfoType?.typeName ?? ''}>
                                <ContactInfoBlock
                                    contactInfo={ci}
                                    isEditing={isEditing}
                                    onUpdate={patch => updateContactInfo(ci.id, patch)}
                                    onDelete={() => deleteContactInfo(ci.id)}
                                />
                            </FormRow>
                        ))}
                        {isEditing && contactInfoTypes.length > 0 && (
                            <FormRow label="">
                                <Box display="flex" gap={1}>
                                    <FormControl size="small" sx={{ flex: 1 }}>
                                        <Select
                                            value={newContactInfoTypeId}
                                            onChange={e => setNewContactInfoTypeId(e.target.value as number)}
                                            displayEmpty
                                        >
                                            <MenuItem value=""><em>{t('contactInfo.chooseType')}</em></MenuItem>
                                            {contactInfoTypes.map(ct => (
                                                <MenuItem key={ct.id} value={ct.id}>{ct.typeName}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <Button size="small" variant="contained" color="secondary"
                                        onClick={addContactInfo} disabled={newContactInfoTypeId === ''}>
                                        +
                                    </Button>
                                </Box>
                            </FormRow>
                        )}
                    </Stack>

                    <Divider />

                    <FormRow label={t('location.users')}>
                        <Box>
                            {activeUsers.map(u => (
                                <Typography sx={sx14} key={u.id}>{u.firstName} {u.lastName}</Typography>
                            ))}
                        </Box>
                    </FormRow>
                </Stack>
            </Box>

            {saving && <Box display="flex" justifyContent="center" p={1}><CircularProgress size={20} /></Box>}
        </Paper>
    );
}

function AddressBlock({
    address, isEditing, onUpdate, onDelete
}: {
    address: Address;
    isEditing: boolean;
    onUpdate: (patch: Partial<Address>) => void;
    onDelete: () => void;
}) {
    if (!isEditing) {
        const parts = [
            [address.addressLine1, address.addressLine2, address.addressLine3].filter(Boolean).join(' '),
            [address.postalCode, address.city].filter(Boolean).join(' '),
            address.country,
        ].filter(Boolean).join(', ');
        return <Typography sx={sx14}>{parts}</Typography>;
    }

    return (
        <Box display="flex" gap={1} alignItems="flex-start">
            <Stack spacing={0.5} flex={1}>
                <TextField size="small" fullWidth placeholder="Line 1"
                    value={address.addressLine1 ?? ''}
                    onChange={e => onUpdate({ addressLine1: e.target.value })} />
                <TextField size="small" fullWidth placeholder="Line 2"
                    value={address.addressLine2 ?? ''}
                    onChange={e => onUpdate({ addressLine2: e.target.value })} />
                <TextField size="small" fullWidth placeholder="Line 3"
                    value={address.addressLine3 ?? ''}
                    onChange={e => onUpdate({ addressLine3: e.target.value })} />
                <Box display="flex" gap={1}>
                    <TextField size="small" sx={{ width: 120 }} placeholder="Postal code"
                        value={address.postalCode ?? ''}
                        onChange={e => onUpdate({ postalCode: e.target.value })} />
                    <TextField size="small" fullWidth placeholder="City"
                        value={address.city ?? ''}
                        onChange={e => onUpdate({ city: e.target.value })} />
                </Box>
                <TextField size="small" fullWidth placeholder="Country"
                    value={address.country ?? ''}
                    onChange={e => onUpdate({ country: e.target.value })} />
            </Stack>
            <IconButton size="small" color="error" onClick={onDelete}>×</IconButton>
        </Box>
    );
}

function ContactInfoBlock({
    contactInfo, isEditing, onUpdate, onDelete
}: {
    contactInfo: ContactInfo;
    isEditing: boolean;
    onUpdate: (patch: Partial<ContactInfo>) => void;
    onDelete: () => void;
}) {
    if (!isEditing) {
        return <Typography sx={sx14}>{contactInfo.value}</Typography>;
    }
    return (
        <Box display="flex" gap={1} alignItems="center">
            <TextField size="small" fullWidth
                value={contactInfo.value ?? ''}
                onChange={e => onUpdate({ value: e.target.value })} />
            <IconButton size="small" color="error" onClick={onDelete}>×</IconButton>
        </Box>
    );
}
